import React, { useState, useEffect, useRef } from 'react';
import { Briefcase, Percent, Car, Coins, ArrowRight, Sparkles, TrendingUp, Shield, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { expertiseItems } from '../../data/mockData';

const iconMap = {
  Briefcase: Briefcase,
  Percent: Percent,
  Car: Car,
  Coins: Coins
};

// Leasing & Loans slideshow images
const leasingImages = [
  { src: '/leasing-loans.png', alt: 'Car Leasing - Keys handover' },
  { src: '/leasing-loans-2.png', alt: 'Car Loan Calculator' },
  { src: '/leasing-loans-3.png', alt: 'Loan Approved' }
];

// Business Loan slideshow images
const businessLoanImages = [
  { src: '/business-loan-1.png', alt: 'Business Loan Approval' },
  { src: '/business-loan-2.png', alt: 'Business Partnership' },
  { src: '/business-loan-3.png', alt: 'Loan Approved Document' }
];

// Auto Loan slideshow images
const autoLoanImages = [
  { src: '/auto-loan-1.png', alt: 'Auto Loan Documents' },
  { src: '/auto-loan-2.png', alt: 'Secure Auto Financing' },
  { src: '/auto-loan-3.png', alt: 'Car Purchase Exchange' }
];

// Gold Loan slideshow images
const goldLoanImages = [
  { src: '/gold-loan-1.png', alt: 'Gold Jewelry' },
  { src: '/gold-loan-2.jpg', alt: 'Gold Investment' },
  { src: '/gold-loan-3.png', alt: 'Gold Cash Exchange' }
];

// Card styles with brand colors on white background
const cardStyles = [
  {
    gradient: 'from-[#2A9D8F]/10 via-[#2A9D8F]/5 to-white',
    border: 'border-[#2A9D8F]/20',
    hoverBorder: 'hover:border-[#2A9D8F]/50',
    iconBg: 'bg-gradient-to-br from-[#2A9D8F] to-[#004a8b]',
    accent: 'bg-[#2A9D8F]',
    glow: 'hover:shadow-[#2A9D8F]/15',
    link: '/products/leasing-loans'
  },
  {
    gradient: 'from-emerald-500/10 via-emerald-500/5 to-white',
    border: 'border-emerald-500/20',
    hoverBorder: 'hover:border-emerald-500/50',
    iconBg: 'bg-gradient-to-br from-emerald-500 to-teal-600',
    accent: 'bg-emerald-500',
    glow: 'hover:shadow-emerald-500/15',
    link: '/products/business-loan'
  },
  {
    gradient: 'from-[#0077cc]/10 via-[#0077cc]/5 to-white',
    border: 'border-[#0077cc]/20',
    hoverBorder: 'hover:border-[#0077cc]/50',
    iconBg: 'bg-gradient-to-br from-[#0077cc] to-[#0099ee]',
    accent: 'bg-[#0077cc]',
    glow: 'hover:shadow-[#0077cc]/15',
    link: '/products/auto-loan'
  },
  {
    gradient: 'from-amber-500/10 via-amber-500/5 to-white',
    border: 'border-amber-500/20',
    hoverBorder: 'hover:border-amber-500/50',
    iconBg: 'bg-gradient-to-br from-amber-500 to-yellow-500',
    accent: 'bg-amber-500',
    glow: 'hover:shadow-amber-500/15',
    link: '/products/gold-cash'
  }
];

// Stats with icons
const stats = [
  { value: '6+', label: 'Years Experience', icon: Clock, color: 'from-[#2A9D8F] to-[#0088dd]' },
  { value: '12+', label: 'Branches Nationwide', icon: TrendingUp, color: 'from-emerald-500 to-teal-500' },
  { value: '5K+', label: 'Happy Customers', icon: Sparkles, color: 'from-[#2A9D8F] to-[#0099ee]' },
  { value: '24/7', label: 'Customer Support', icon: Shield, color: 'from-amber-500 to-yellow-500' }
];

const ExpertiseSection = () => {
  const [hoveredCard, setHoveredCard] = useState(null);
  const [currentLeasingIndex, setCurrentLeasingIndex] = useState(0);
  const [currentBusinessIndex, setCurrentBusinessIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  // Intersection Observer to pause transitions when off-screen
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  // Auto-play slideshow for leasing images
  useEffect(() => {
    if (!isVisible) return;
    const interval = setInterval(() => {
      setCurrentLeasingIndex((prev) => (prev + 1) % leasingImages.length);
    }, 3000); // Change image every 3 seconds

    return () => clearInterval(interval);
  }, [isVisible]);

  // Auto-play slideshow for business loan images
  useEffect(() => {
    if (!isVisible) return;
    const interval = setInterval(() => {
      setCurrentBusinessIndex((prev) => (prev + 1) % businessLoanImages.length);
    }, 3500); // Change image every 3.5 seconds (slightly offset)

    return () => clearInterval(interval);
  }, [isVisible]);

  // Auto-play slideshow for auto loan images
  const [currentAutoIndex, setCurrentAutoIndex] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    const interval = setInterval(() => {
      setCurrentAutoIndex((prev) => (prev + 1) % autoLoanImages.length);
    }, 4000); // Change image every 4 seconds (slightly offset)

    return () => clearInterval(interval);
  }, [isVisible]);

  // Auto-play slideshow for gold loan images
  const [currentGoldIndex, setCurrentGoldIndex] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    const interval = setInterval(() => {
      setCurrentGoldIndex((prev) => (prev + 1) % goldLoanImages.length);
    }, 4500); // Change image every 4.5 seconds (slightly offset)

    return () => clearInterval(interval);
  }, [isVisible]);

  return (
    <section 
      id="expertise" 
      ref={sectionRef}
      className="relative pt-10 pb-8 md:pt-12 md:pb-12 overflow-hidden bg-white"
    >
      {/* Subtle background pattern */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, #2A9D8F 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <div className="text-center mb-14 md:mb-16">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#2A9D8F]/10 border border-[#2A9D8F]/20 rounded-full mb-6">
            <Sparkles size={14} className="text-[#2A9D8F]" />
            <span className="text-sm text-[#2A9D8F] font-medium tracking-wide">Why Choose Us</span>
          </div>

          {/* Title */}
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-5 tracking-tight">
            Our{' '}
            <span className="relative">
              <span className="text-[#2A9D8F]">
                Expertise
              </span>
              <div className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-[#2A9D8F] to-[#0099ee] rounded-full" />
            </span>
          </h2>

          {/* Subtitle */}
          <p className="text-gray-600 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            Pioneer in providing value-added financial services to Sri Lankans with three decades of trusted excellence.
          </p>
        </div>

        {/* Expertise Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 lg:gap-8 mb-16 md:mb-20">
          {expertiseItems.map((item, index) => {
            const Icon = iconMap[item.icon];
            const style = cardStyles[index % cardStyles.length];
            const isHovered = hoveredCard === index;

            return (
              <Link
                key={item.id}
                to={style.link}
                onMouseEnter={() => setHoveredCard(index)}
                onMouseLeave={() => setHoveredCard(null)}
                className={`group relative overflow-hidden rounded-2xl lg:rounded-3xl bg-gradient-to-br ${style.gradient} border ${style.border} ${style.hoverBorder} transition-all duration-500 hover:shadow-xl ${style.glow}`}
                style={{
                  transform: isHovered ? 'translateY(-8px) scale(1.02)' : 'translateY(0) scale(1)',
                }}
              >
                {/* Card hover overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                {/* Content Container */}
                <div className="relative z-10 p-6 md:p-8 lg:p-10">
                  <div className="flex flex-col lg:flex-row h-full gap-6">
                    {/* Left Content */}
                    <div className="flex flex-col flex-1">
                      {/* Icon */}
                      <div className={`inline-flex w-14 h-14 md:w-16 md:h-16 items-center justify-center rounded-xl md:rounded-2xl ${style.iconBg} shadow-lg mb-5 md:mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-500`}>
                        <Icon size={28} className="text-white" strokeWidth={1.5} />
                      </div>

                      {/* Title */}
                      <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 tracking-tight">
                        {item.title}
                      </h3>

                      {/* Description */}
                      <p className="text-gray-600 text-sm md:text-base leading-relaxed whitespace-pre-line mb-6 flex-grow">
                        {item.description}
                      </p>

                      {/* CTA Link */}
                      <div className="flex items-center gap-2 text-[#2A9D8F] group-hover:text-[#004a8b] transition-all duration-300">
                        <span className="text-sm font-semibold tracking-wide">Learn more</span>
                        <div className="w-6 h-6 rounded-full bg-[#2A9D8F]/10 flex items-center justify-center group-hover:bg-[#2A9D8F] group-hover:text-white group-hover:translate-x-1 transition-all duration-300">
                          <ArrowRight size={14} />
                        </div>
                      </div>
                    </div>

                    {/* Image Slideshow for Leasing and Loans card (first card) */}
                    {index === 0 && (
                      <div className="relative lg:w-[45%] flex-shrink-0">
                        <div className="relative overflow-hidden rounded-xl lg:rounded-2xl shadow-lg group-hover:shadow-xl transition-all duration-500 h-40 lg:h-56">
                          {/* Image slideshow */}
                          {leasingImages.map((image, imgIndex) => (
                            <img
                              key={imgIndex}
                              src={image.src}
                              alt={image.alt}
                              loading="lazy"
                              className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-in-out ${imgIndex === currentLeasingIndex
                                ? 'opacity-100 scale-100'
                                : 'opacity-0 scale-105'
                                }`}
                            />
                          ))}
                          {/* Gradient overlay */}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />

                          {/* Slide indicators */}
                          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                            {leasingImages.map((_, dotIndex) => (
                              <button
                                key={dotIndex}
                                onClick={(e) => {
                                  e.preventDefault();
                                  setCurrentLeasingIndex(dotIndex);
                                }}
                                className={`w-2 h-2 rounded-full transition-all duration-300 ${dotIndex === currentLeasingIndex
                                  ? 'w-6 bg-white'
                                  : 'bg-white/50 hover:bg-white/80'
                                  }`}
                                aria-label={`Go to slide ${dotIndex + 1}`}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Image Slideshow for Business Loan card (second card) */}
                    {index === 1 && (
                      <div className="relative lg:w-[45%] flex-shrink-0">
                        <div className="relative overflow-hidden rounded-xl lg:rounded-2xl shadow-lg group-hover:shadow-xl transition-all duration-500 h-40 lg:h-56">
                          {/* Image slideshow */}
                          {businessLoanImages.map((image, imgIndex) => (
                            <img
                              key={imgIndex}
                              src={image.src}
                              alt={image.alt}
                              className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-in-out ${imgIndex === currentBusinessIndex
                                ? 'opacity-100 scale-100'
                                : 'opacity-0 scale-105'
                                }`}
                            />
                          ))}
                          {/* Gradient overlay */}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />

                          {/* Slide indicators */}
                          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                            {businessLoanImages.map((_, dotIndex) => (
                              <button
                                key={dotIndex}
                                onClick={(e) => {
                                  e.preventDefault();
                                  setCurrentBusinessIndex(dotIndex);
                                }}
                                className={`w-2 h-2 rounded-full transition-all duration-300 ${dotIndex === currentBusinessIndex
                                  ? 'w-6 bg-white'
                                  : 'bg-white/50 hover:bg-white/80'
                                  }`}
                                aria-label={`Go to slide ${dotIndex + 1}`}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Image Slideshow for Auto Loan card (third card) */}
                    {index === 2 && (
                      <div className="relative lg:w-[45%] flex-shrink-0">
                        <div className="relative overflow-hidden rounded-xl lg:rounded-2xl shadow-lg group-hover:shadow-xl transition-all duration-500 h-40 lg:h-56">
                          {/* Image slideshow */}
                          {autoLoanImages.map((image, imgIndex) => (
                            <img
                              key={imgIndex}
                              src={image.src}
                              alt={image.alt}
                              className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-in-out ${imgIndex === currentAutoIndex
                                ? 'opacity-100 scale-100'
                                : 'opacity-0 scale-105'
                                }`}
                            />
                          ))}
                          {/* Gradient overlay */}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />

                          {/* Slide indicators */}
                          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                            {autoLoanImages.map((_, dotIndex) => (
                              <button
                                key={dotIndex}
                                onClick={(e) => {
                                  e.preventDefault();
                                  setCurrentAutoIndex(dotIndex);
                                }}
                                className={`w-2 h-2 rounded-full transition-all duration-300 ${dotIndex === currentAutoIndex
                                  ? 'w-6 bg-white'
                                  : 'bg-white/50 hover:bg-white/80'
                                  }`}
                                aria-label={`Go to slide ${dotIndex + 1}`}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Image Slideshow for Gold Loan card (fourth card) */}
                    {index === 3 && (
                      <div className="relative lg:w-[45%] flex-shrink-0">
                        <div className="relative overflow-hidden rounded-xl lg:rounded-2xl shadow-lg group-hover:shadow-xl transition-all duration-500 h-40 lg:h-56">
                          {/* Image slideshow */}
                          {goldLoanImages.map((image, imgIndex) => (
                            <img
                              key={imgIndex}
                              src={image.src}
                              alt={image.alt}
                              className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-in-out ${imgIndex === currentGoldIndex
                                ? 'opacity-100 scale-100'
                                : 'opacity-0 scale-105'
                                }`}
                            />
                          ))}
                          {/* Gradient overlay */}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />

                          {/* Slide indicators */}
                          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                            {goldLoanImages.map((_, dotIndex) => (
                              <button
                                key={dotIndex}
                                onClick={(e) => {
                                  e.preventDefault();
                                  setCurrentGoldIndex(dotIndex);
                                }}
                                className={`w-2 h-2 rounded-full transition-all duration-300 ${dotIndex === currentGoldIndex
                                  ? 'w-6 bg-white'
                                  : 'bg-white/50 hover:bg-white/80'
                                  }`}
                                aria-label={`Go to slide ${dotIndex + 1}`}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Decorative corner accent */}
                <div className={`absolute -bottom-12 -right-12 w-40 h-40 ${style.accent} opacity-5 rounded-full blur-2xl group-hover:opacity-10 transition-all duration-700`} />
              </Link>
            );
          })}
        </div>

        {/* Stats Section */}
        <div className="relative">
          {/* Divider line with gradient */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />

          {/* Stats Grid */}
          <div className="pt-12 md:pt-16">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
              {stats.map((stat, index) => {
                const StatIcon = stat.icon;
                return (
                  <div
                    key={index}
                    className="group text-center"
                  >
                    {/* Stat value with animated icon */}
                    <div className="relative inline-flex flex-col items-center">
                      {/* Animated floating icon */}
                      <div
                        className="mb-4 relative"
                        style={{
                          animation: `float 2s ease-in-out infinite`,
                          animationDelay: `${index * 0.3}s`
                        }}
                      >
                        <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-gradient-to-br from-[#D4AF37]/10 to-[#14B8A6]/10 flex items-center justify-center group-hover:scale-110 transition-all duration-500 shadow-lg group-hover:shadow-xl border border-[#D4AF37]/20">
                          <div className="animate-spin-hover transition-transform duration-500">
                            <StatIcon
                              size={32}
                              className="text-[#D4AF37] group-hover:text-[#14B8A6] transition-colors duration-300"
                              strokeWidth={1.5}
                            />
                          </div>
                        </div>
                        {/* Glow effect */}
                        <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[#D4AF37]/20 to-[#14B8A6]/20 blur-xl opacity-0 group-hover:opacity-60 transition-opacity duration-500 -z-10" />
                      </div>

                      {/* Number */}
                      <div className={`text-3xl sm:text-4xl md:text-5xl font-bold bg-gradient-to-r ${stat.color} bg-clip-text text-transparent mb-2 group-hover:scale-110 transition-transform duration-300`}>
                        {stat.value}
                      </div>

                      {/* Label */}
                      <div className="text-xs sm:text-sm text-gray-500 font-medium tracking-wide">
                        {stat.label}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ExpertiseSection;

